import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Trash2, Download, ImageUp } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, mediaUrl } from "@/lib/api";
import { Eyebrow, GoldButton } from "@/components/site/motion";
import { useSEO } from "@/hooks/useSEO";

const inputCls =
  "w-full border-b border-ink/20 bg-transparent py-3 text-sm text-ink placeholder:text-smoke focus:border-gold focus:outline-none transition-colors duration-300";

const CATEGORIES = ["News", "Blog", "Events", "Partnerships"];

export default function Admin() {
  useSEO("Enquiries — Hapo Group Admin", "Contact enquiries and newsletter subscribers received by the Hapo Group website.");
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [subs, setSubs] = useState([]);
  const [cvs, setCvs] = useState([]);
  const [posts, setPosts] = useState([]);
  const [error, setError] = useState(null);
  const [post, setPost] = useState({ category: "News", title: "", tag: "", date: "", image: "", link: "", body: "" });
  const [publishing, setPublishing] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const imageInputRef = useRef(null);
  const [vacancies, setVacancies] = useState([]);
  const [vacancy, setVacancy] = useState({ title: "", team: "", location: "" });
  const [vacancySaving, setVacancySaving] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("hapo_admin_token")) {
      navigate("/admin/login", { replace: true });
      return;
    }
    const on401 = () => {
      localStorage.removeItem("hapo_admin_token");
      navigate("/admin/login", { replace: true });
    };
    const guard = (p) =>
      p.catch((e) => {
        if (e.response?.status === 401) on401();
        else setError("Some data could not be loaded.");
        return null;
      });
    guard(api.enquiries().then((r) => r && setEnquiries(r.data)));
    guard(api.subscribers().then((r) => r && setSubs(r.data)));
    guard(api.listCvs().then((r) => r && setCvs(r.data)));
    api
      .insights()
      .then((r) => setPosts(r.data))
      .catch(() => {});
    api
      .vacancies()
      .then((r) => setVacancies(r.data))
      .catch(() => {});
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("hapo_admin_token");
    navigate("/admin/login");
  };

  const updatePost = (key) => (e) => setPost((p) => ({ ...p, [key]: e.target.value }));

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.uploadInsightImage(fd);
      setPost((p) => ({ ...p, image: data.url }));
      toast.success("Image uploaded — it will be used as the featured image.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Image upload failed — please try again.");
    } finally {
      setImageUploading(false);
      e.target.value = "";
    }
  };

  const bodyTextRef = useRef(null);
  const bodyImageInputRef = useRef(null);
  const [bodyImageUploading, setBodyImageUploading] = useState(false);

  const insertBodyImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBodyImageUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.uploadInsightImage(fd);
      setPost((p) => {
        const ta = bodyTextRef.current;
        const start = ta ? (ta.selectionStart ?? p.body.length) : p.body.length;
        const end = ta ? (ta.selectionEnd ?? start) : start;
        const snippet = `![image](${data.url})`;
        const before = p.body.slice(0, start).replace(/\s*$/, "");
        const after = p.body.slice(end).replace(/^\s*/, "");
        return { ...p, body: [before, snippet, after].filter(Boolean).join("\n\n") };
      });
      toast.success("Image inserted into the article body.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Image upload failed — please try again.");
    } finally {
      setBodyImageUploading(false);
      e.target.value = "";
    }
  };

  const publish = async (e) => {
    e.preventDefault();
    setPublishing(true);
    try {
      const payload = { ...post, image: post.image || null, link: post.link || null, body: post.body || null, tag: post.tag || post.category.toUpperCase() };
      const { data } = await api.createPost(payload);
      setPosts((prev) => [data, ...prev]);
      setPost({ category: "News", title: "", tag: "", date: "", image: "", link: "", body: "" });
      toast.success(`Published to ${data.category}.`);
    } catch (err) {
      toast.error("Could not publish — please try again.");
    } finally {
      setPublishing(false);
    }
  };

  const removePost = async (id) => {
    try {
      await api.deletePost(id);
      setPosts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Post deleted.");
    } catch {
      toast.error("Could not delete the post.");
    }
  };

  const removeEnquiry = async (id) => {
    try {
      await api.deleteEnquiry(id);
      setEnquiries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Could not delete the enquiry.");
    }
  };

  const updateVacancy = (key) => (e) => setVacancy((v) => ({ ...v, [key]: e.target.value }));

  const publishVacancy = async (e) => {
    e.preventDefault();
    setVacancySaving(true);
    try {
      const { data } = await api.createVacancy(vacancy);
      setVacancies((prev) => [...prev, data]);
      setVacancy({ title: "", team: "", location: "" });
      toast.success("Role published — it's live on the Careers page.");
    } catch {
      toast.error("Could not publish the role — please try again.");
    } finally {
      setVacancySaving(false);
    }
  };

  const removeVacancy = async (id) => {
    try {
      await api.deleteVacancy(id);
      setVacancies((prev) => prev.filter((v) => v.id !== id));
      toast.success("Role taken down.");
    } catch {
      toast.error("Could not remove the role.");
    }
  };

  return (
    <section className="min-h-screen bg-mist px-6 pb-24 pt-36 lg:px-12" data-testid="admin-page">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <Eyebrow>Admin</Eyebrow>
          <h1 className="mt-4 text-4xl font-extrabold tracking-tighter text-ink sm:text-5xl">Site Manager</h1>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-smoke">
            Publish insights, review CVs, and track every contact enquiry and newsletter signup. Enquiries are also
            emailed to info@hapogroup.co.za.
          </p>
        </div>
        <button
          type="button"
          onClick={logout}
          data-testid="admin-logout-button"
          className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-6 py-3 text-[10px] font-bold uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:border-gold hover:text-gold"
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </button>
      </div>
      {error && (
        <p className="mt-8 text-sm font-semibold text-red-600" data-testid="admin-error">
          {error}
        </p>
      )}

      {/* Publish insight */}
      <h2 className="mt-16 text-2xl font-extrabold tracking-tighter text-ink">Publish an insight</h2>
      <form onSubmit={publish} className="mt-6 border border-ink/10 bg-white p-8" data-testid="post-form">
        <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-4">
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Section *</span>
            <select value={post.category} onChange={updatePost("category")} data-testid="post-category-select" className={`${inputCls} cursor-pointer`}>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="block sm:col-span-1 lg:col-span-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Headline *</span>
            <input required value={post.title} onChange={updatePost("title")} data-testid="post-title-input" className={inputCls} placeholder="Post headline" />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Date label</span>
            <input value={post.date} onChange={updatePost("date")} data-testid="post-date-input" className={inputCls} placeholder="e.g. March 2026" />
          </label>
        </div>
        <div className="mt-7 grid gap-7 sm:grid-cols-2">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Featured image (optional)</span>
            <div className="mt-3 flex flex-wrap items-center gap-4">
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={pickImage}
                className="hidden"
                data-testid="post-image-file-input"
              />
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                disabled={imageUploading}
                data-testid="post-image-upload-button"
                className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.18em] text-ink transition-colors duration-300 hover:border-gold hover:text-gold disabled:opacity-50"
              >
                <ImageUp className="h-3.5 w-3.5" />
                {imageUploading ? "Uploading..." : "Upload from Computer"}
              </button>
              {post.image && (
                <img
                  src={mediaUrl(post.image)}
                  alt="Featured image preview"
                  data-testid="post-image-preview"
                  className="h-14 w-24 border border-ink/10 object-cover"
                />
              )}
            </div>
            <input
              value={post.image}
              onChange={updatePost("image")}
              data-testid="post-image-input"
              className={`${inputCls} mt-4`}
              placeholder="...or paste an image URL"
            />
          </div>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Link (optional)</span>
            <input value={post.link} onChange={updatePost("link")} data-testid="post-link-input" className={inputCls} placeholder="https://..." />
          </label>
        </div>
        <div className="mt-7">
          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Article body (optional)</span>
          <textarea
            ref={bodyTextRef}
            rows={6}
            value={post.body}
            onChange={updatePost("body")}
            data-testid="post-body-input"
            className={`${inputCls} resize-none`}
            placeholder="Full article text — separate paragraphs with a blank line. If filled in and no link is set, the card opens a full article page on the site."
          />
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <input
              ref={bodyImageInputRef}
              type="file"
              accept="image/*"
              onChange={insertBodyImage}
              className="hidden"
              data-testid="post-body-image-file-input"
            />
            <button
              type="button"
              onClick={() => bodyImageInputRef.current?.click()}
              disabled={bodyImageUploading}
              data-testid="post-body-image-upload-button"
              className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.18em] text-ink transition-colors duration-300 hover:border-gold hover:text-gold disabled:opacity-50"
            >
              <ImageUp className="h-3.5 w-3.5" />
              {bodyImageUploading ? "Uploading..." : "Insert Image at Cursor"}
            </button>
          </div>
          <p className="mt-2 text-[11px] leading-relaxed text-smoke">
            Formatting: <span className="font-semibold text-ink">**bold**</span>,{" "}
            <span className="font-semibold text-ink">[link text](https://...)</span>,{" "}
            <span className="font-semibold text-ink">## Subheading</span> at the start of a paragraph,{" "}
            <span className="font-semibold text-ink">![caption](image URL)</span> on its own line — or use Insert Image above,{" "}
            <span className="font-semibold text-ink">- List item</span> lines for bullet lists.
          </p>
        </div>
        <div className="mt-8">
          <GoldButton type="submit" testId="post-submit-button">
            {publishing ? "Publishing..." : "Publish"}
          </GoldButton>
        </div>
      </form>

      <div className="mt-8 border border-ink/10 bg-white" data-testid="posts-list">
        {posts.length === 0 && <p className="px-6 py-8 text-sm text-smoke">No posts published yet — the homepage shows placeholder cards until you publish.</p>}
        {posts.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center justify-between gap-4 border-b border-ink/5 px-6 py-4">
            <div className="flex items-center gap-4">
              {p.image && (
                <img src={mediaUrl(p.image)} alt="" className="h-12 w-20 border border-ink/10 object-cover" />
              )}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold">{p.category}</p>
                <p className="mt-1 text-sm font-semibold text-ink">{p.title}</p>
                <p className="mt-1 text-xs text-smoke">{p.date}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => removePost(p.id)}
              data-testid={`delete-post-${p.id}`}
              className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-smoke transition-colors duration-300 hover:border-red-500 hover:text-red-600"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </button>
          </div>
        ))}
      </div>

      {/* Careers — open roles */}
      <h2 className="mt-16 text-2xl font-extrabold tracking-tighter text-ink">Careers — open roles</h2>
      <form onSubmit={publishVacancy} className="mt-6 border border-ink/10 bg-white p-6 lg:p-8" data-testid="vacancy-form">
        <div className="grid gap-7 sm:grid-cols-3">
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Role title *</span>
            <input required value={vacancy.title} onChange={updateVacancy("title")} data-testid="vacancy-title-input" className={inputCls} placeholder="e.g. Software Developers" />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Team</span>
            <input value={vacancy.team} onChange={updateVacancy("team")} data-testid="vacancy-team-input" className={inputCls} placeholder="e.g. Hapo Cloud & Hapo Intelligence" />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Location</span>
            <input value={vacancy.location} onChange={updateVacancy("location")} data-testid="vacancy-location-input" className={inputCls} placeholder="e.g. Cape Town · Full-time" />
          </label>
        </div>
        <div className="mt-8">
          <GoldButton type="submit" testId="vacancy-publish-button">
            {vacancySaving ? "Publishing..." : "Publish Role"}
          </GoldButton>
        </div>
      </form>
      <div className="mt-8 border border-ink/10 bg-white" data-testid="vacancies-list">
        {vacancies.length === 0 && <p className="px-6 py-8 text-sm text-smoke">No open roles — the Careers page will say so and invite CVs.</p>}
        {vacancies.map((v) => (
          <div key={v.id} className="flex flex-wrap items-center justify-between gap-4 border-b border-ink/5 px-6 py-4">
            <div>
              <p className="text-sm font-semibold text-ink">{v.title}</p>
              <p className="mt-1 text-xs text-smoke">
                {v.team}
                {v.team && v.location ? " · " : ""}
                {v.location}
              </p>
            </div>
            <button
              type="button"
              onClick={() => removeVacancy(v.id)}
              data-testid={`delete-vacancy-${v.id}`}
              className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-smoke transition-colors duration-300 hover:border-red-500 hover:text-red-600"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Take Down
            </button>
          </div>
        ))}
      </div>

      {/* CVs */}
      <h2 className="mt-16 text-2xl font-extrabold tracking-tighter text-ink">CV uploads</h2>
      <div className="mt-6 border border-ink/10 bg-white" data-testid="cvs-list">
        {cvs.length === 0 && <p className="px-6 py-8 text-sm text-smoke">No CVs uploaded yet.</p>}
        {cvs.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center justify-between gap-4 border-b border-ink/5 px-6 py-4">
            <div>
              <p className="text-sm font-semibold text-ink">{c.name}</p>
              <p className="mt-1 text-xs text-smoke">
                {c.email} · {c.interest} · {new Date(c.created_at).toLocaleDateString()}
              </p>
            </div>
            <a
              href={fileUrl(c.storage_path)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`cv-download-${c.id}`}
              className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-ink transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              <Download className="h-3.5 w-3.5" />
              {c.original_filename}
            </a>
          </div>
        ))}
      </div>

      {/* Enquiries */}
      <h2 className="mt-16 text-2xl font-extrabold tracking-tighter text-ink">Enquiries</h2>
      <div className="mt-6 overflow-x-auto border border-ink/10 bg-white" data-testid="admin-enquiries-table">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead>
            <tr className="border-b border-ink/10 text-[10px] font-bold uppercase tracking-[0.2em] text-smoke">
              <th className="px-6 py-4">Date</th>
              <th className="px-6 py-4">Name</th>
              <th className="px-6 py-4">Email</th>
              <th className="px-6 py-4">Regarding</th>
              <th className="max-w-md px-6 py-4">Message</th>
              <th className="px-6 py-4">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {enquiries.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-10 text-center text-smoke">
                  No enquiries yet.
                </td>
              </tr>
            )}
            {enquiries.map((e) => (
              <tr key={e.id} className="border-b border-ink/5 align-top">
                <td className="whitespace-nowrap px-6 py-4 text-smoke">{new Date(e.created_at).toLocaleString()}</td>
                <td className="px-6 py-4 font-semibold text-ink">{e.name}</td>
                <td className="px-6 py-4 text-ink">{e.email}</td>
                <td className="px-6 py-4 text-ink">{e.company}</td>
                <td className="max-w-md whitespace-pre-wrap px-6 py-4 text-smoke">{e.message}</td>
                <td className="px-6 py-4 text-right">
                  <button
                    type="button"
                    onClick={() => removeEnquiry(e.id)}
                    data-testid={`delete-enquiry-${e.id}`}
                    aria-label={`Delete enquiry from ${e.name}`}
                    className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-smoke transition-colors duration-300 hover:border-red-500 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="mt-16 text-2xl font-extrabold tracking-tighter text-ink">Newsletter subscribers</h2>
      <div className="mt-6 flex flex-wrap gap-3" data-testid="admin-subscribers-list">
        {subs.length === 0 && <p className="text-sm text-smoke">No subscribers yet.</p>}
        {subs.map((s) => (
          <span key={s.email} className="rounded-full border border-ink/15 bg-white px-4 py-2 text-xs text-ink">
            {s.email}
          </span>
        ))}
      </div>
    </section>
  );
}
